import React, { createContext, useCallback, useContext, useState } from 'react'

type JwtPayload = Record<string, unknown> | null

type AuthContextValue = {
  payload: JwtPayload
  token: string | null
  loading: boolean
  error: string | null
  setToken: (token: string | null) => void
  validate: (token: string) => Promise<boolean>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [payload, setPayload] = useState<JwtPayload>(null)
  const [token, setTokenState] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const setToken = useCallback((nextToken: string | null) => {
    setTokenState(nextToken)
    if (typeof window === 'undefined') {
      return
    }
    if (nextToken) {
      window.sessionStorage.setItem('reportingPushToken', nextToken)
    } else {
      window.sessionStorage.removeItem('reportingPushToken')
    }
  }, [])

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const params = new URLSearchParams(window.location.search)
    const pushToken = params.get('pushToken')
    if (pushToken) {
      setTokenState(pushToken)
      window.sessionStorage.setItem('reportingPushToken', pushToken)
      return
    }
    const stored = window.sessionStorage.getItem('reportingPushToken')
    if (stored) {
      setTokenState(stored)
    }
  }, [])

  const validateRequestId = React.useRef(0)

  const validate = useCallback(
    async (token: string) => {
      if (!token) return false
      const requestId = ++validateRequestId.current
      setLoading(true)
      setError(null)
      let success = false
      try {
        const res = await fetch('/api/auth/validate-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })

        if (!res.ok) {
          throw new Error('Validation failed')
        }

        const data = await res.json()
        if (requestId !== validateRequestId.current) {
          return false
        }

        setPayload(data.payload || null)
        setToken(token)
        success = true
      } catch (_e) {
        if (requestId !== validateRequestId.current) {
          return false
        }
        setPayload(null)
        setToken(null)
        setError('Unable to validate session')
      } finally {
        if (requestId === validateRequestId.current) {
          setLoading(false)
        }
      }
      return success
    },
    [setToken],
  )

  return (
    <AuthContext.Provider
      value={{
        payload,
        token,
        loading,
        error,
        setToken,
        validate,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    if (typeof window === 'undefined') {
      // @ts-ignore
      return {
        payload: null,
        loading: false,
        error: null,
        validate: async (_token: string) => false,
      }
    }

    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
